import { type DesignComposerOperation, describeDesignComposerOperation } from "@afar-research/afar";
import type { DesignSystemMention } from "@afar-research/contracts";
import {
  FilePlus2Icon,
  ImagePlusIcon,
  PaletteIcon,
  PencilLineIcon,
  SparklesIcon,
  TagIcon,
  Trash2Icon,
} from "lucide-react";

import { cn } from "~/lib/utils";

/**
 * How a Design Operation looks, wherever it is drawn. The composer picks a mode
 * with it and the timeline draws an operation that was already sent with it, so
 * the icon and the tint live here rather than in either surface — a queued
 * Rename and the composer that queued it must not disagree about its colour.
 *
 * Presentation only. Every mode's copy — label, short label, headline, blurb —
 * is domain and lives in `@afar-research/afar`; the hue is not, and does not.
 */
export const DESIGN_MODE_ICON: Readonly<Record<DesignComposerOperation, typeof PaletteIcon>> = {
  "create-design": PaletteIcon,
  "update-design": PencilLineIcon,
  "add-design-template": FilePlus2Icon,
  "add-design-example": ImagePlusIcon,
  "create-design-sample": SparklesIcon,
  "rename-design-system": TagIcon,
  "delete-design-system": Trash2Icon,
};

export interface DesignModeTint {
  /** Icon and label, on a selected rail tab, a menu row, and a sent operation. */
  readonly text: string;
  readonly surface: string;
  /** A sent operation's rule against the words underneath it. */
  readonly border: string;
}

/**
 * Three voices, not seven. The five library operations build a Design System
 * and share one calm accent; Rename changes its identity; Delete reads as a
 * loss. A hue per operation would make the timeline a mood ring.
 */
const LIBRARY_TINT: DesignModeTint = {
  text: "text-violet-800 dark:text-violet-200",
  surface: "bg-violet-500/10",
  border: "border-violet-500/35",
};

const IDENTITY_TINT: DesignModeTint = {
  text: "text-sky-800 dark:text-sky-200",
  surface: "bg-sky-500/10",
  border: "border-sky-500/35",
};

const DESTRUCTIVE_TINT: DesignModeTint = {
  text: "text-rose-800 dark:text-rose-200",
  surface: "bg-rose-500/10",
  border: "border-rose-500/40",
};

export const DESIGN_MODE_TINT: Readonly<Record<DesignComposerOperation, DesignModeTint>> = {
  "create-design": LIBRARY_TINT,
  "update-design": LIBRARY_TINT,
  "add-design-template": LIBRARY_TINT,
  "add-design-example": LIBRARY_TINT,
  "create-design-sample": LIBRARY_TINT,
  "rename-design-system": IDENTITY_TINT,
  "delete-design-system": DESTRUCTIVE_TINT,
};

/**
 * A sent Design Operation's chrome: the operation named again on the top edge
 * of whatever already drew the user's words. Icon, operation, and the target it
 * points at — and nothing else, because the brief underneath is the message and
 * this is the frame around it.
 *
 * It carries the target so the ordinary Design System Mention chip does not
 * have to: on an operation surface the two would name the same Design System
 * twice, which is the single clearest thing the #964 prototype showed.
 *
 * `compact` is the pending card's miniature — the short label, because the
 * stack is narrow and the operation still has to stay named.
 */
export function DesignOperationStrip(props: {
  readonly mode: DesignComposerOperation;
  readonly target: DesignSystemMention | null;
  readonly compact?: boolean;
  readonly className?: string;
}) {
  const descriptor = describeDesignComposerOperation(props.mode);
  const tint = DESIGN_MODE_TINT[props.mode];
  const Icon = DESIGN_MODE_ICON[props.mode];
  return (
    <div
      className={cn(
        "flex items-center gap-1.5 border-b font-medium",
        props.compact ? "px-2 py-1 text-[11px]" : "px-3 py-1.5 text-xs",
        tint.surface,
        tint.border,
        tint.text,
        props.className,
      )}
      data-design-operation-strip={props.mode}
    >
      <Icon aria-hidden="true" className="size-3.5 shrink-0" />
      <span className="truncate">{props.compact ? descriptor.shortLabel : descriptor.label}</span>
      {props.target === null ? null : (
        <>
          <span aria-hidden="true" className="opacity-50">
            →
          </span>
          <span className="truncate opacity-90" title={props.target.path}>
            @{props.target.name}
          </span>
        </>
      )}
    </div>
  );
}
